import React, { useEffect, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { connector } from '../services/api';
import { capitalizeError } from '../utils/errorUtils';
import './FineTuningGraphPage.css';

interface LineageNode {
  id: string;
  label?: string;
  type?: string;
  is_head?: boolean;
  [key: string]: any;
}

interface LineageEdge {
  id?: string;
  from_id: string;
  to_id: string;
  label?: string;
  [key: string]: any;
}

interface LineageData {
  nodes?: LineageNode[];
  edges?: LineageEdge[];
  meta?: { nodes_count?: number; edges_count?: number };
  [key: string]: any;
}

interface FineTuningGraphPanelProps {
  integrationId: string | null;
}

const FineTuningGraphPanel: React.FC<FineTuningGraphPanelProps> = ({ integrationId }) => {
  const [graphLoading, setGraphLoading] = React.useState(false);
  const [graphError, setGraphError] = React.useState<string | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (!integrationId) {
      setNodes([]);
      setEdges([]);
      setGraphError(null);
      return;
    }

    const loadGraph = async () => {
      setGraphLoading(true);
      setGraphError(null);
      try {
        const response = await connector.getModelLineage({
          integration_id: integrationId,
          view: 'ui',
          compact: true,
          latest_per_alias: true,
          limit_nodes: 120,
          limit_edges: 200,
        });

        if (response.success && response.data) {
          const graphData: LineageData = response.data;
          const apiNodes = graphData.nodes || [];
          const apiEdges = graphData.edges || [];

          const initialNodes: Node[] = apiNodes.map((node) => {
            const nodeId = node.id || '';
            const label = node.label || nodeId;
            const isBase = !node.is_head;
            return {
              id: nodeId,
              type: 'default',
              position: { x: 0, y: 0 },
              data: {
                label: (
                  <div className="node-label">
                    <div className="node-title">{label}</div>
                    {node.type && (
                      <div className="node-type">
                        {node.type === 'base' ? 'BASE' : 'FINE_TUNED'}
                      </div>
                    )}
                  </div>
                ),
              },
              style: {
                background: isBase ? '#E3F0FB' : '#E3F0FB',
                border: isBase ? '2px solid #0099FA' : '2px solid #FF8300',
                borderRadius: '8px',
                padding: '10px',
                minWidth: '150px',
                fontSize: '12px',
                fontWeight: isBase ? '600' : '500',
              },
            };
          });

          const initialEdges: Edge[] = apiEdges.map((edge, index) => ({
            id: edge.id || `edge-${index}`,
            source: edge.from_id || '',
            target: edge.to_id || '',
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#0099FA', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#0099FA' },
            label: edge.label || '',
            labelStyle: { fill: '#5B7893', fontWeight: 500, fontSize: '11px' },
            labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
          }));

          const dagreGraph = new dagre.graphlib.Graph();
          dagreGraph.setDefaultEdgeLabel(() => ({}));
          dagreGraph.setGraph({ rankdir: 'TB', nodesep: 100, ranksep: 150, marginx: 50, marginy: 50 });
          initialNodes.forEach((node) => dagreGraph.setNode(node.id, { width: 180, height: 80 }));
          initialEdges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));
          dagre.layout(dagreGraph);

          const transformedNodes: Node[] = initialNodes.map((node) => {
            const pos = dagreGraph.node(node.id);
            return {
              ...node,
              position: { x: pos.x - 90, y: pos.y - 40 },
            };
          });

          setNodes(transformedNodes);
          setEdges(initialEdges);
        } else {
          setGraphError('Failed to load graph data');
          setNodes([]);
          setEdges([]);
        }
      } catch (e: any) {
        setGraphError(capitalizeError(e?.response?.data?.error?.message || e?.message) || 'Failed to load graph');
        setNodes([]);
        setEdges([]);
      } finally {
        setGraphLoading(false);
      }
    };

    loadGraph();
  }, [integrationId, setNodes, setEdges]);

  const onConnect = useCallback((params: Connection) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  if (!integrationId) {
    return null;
  }

  return (
    <div id="fine-tuning-graph-panel-root" style={{ marginTop: 24 }}>
      <div className="fine-tuning-graph-controls" id="fine-tuning-graph-intro" style={{ marginBottom: 16 }}>
        <p style={{ margin: 0, fontSize: 14, color: '#5B7893' }}>
          Lineage and relationships between base and fine-tuned models for the selected integration.
        </p>
      </div>
      <div className="graph-container" id="fine-tuning-graph-container" style={{ minHeight: 400 }}>
        {graphLoading && (
          <div className="graph-loading">
            <div className="spinner"></div>
            <p>Loading graph...</p>
          </div>
        )}
        {graphError && (
          <div className="graph-error">
            <p>{graphError}</p>
          </div>
        )}
        {!graphLoading && !graphError && nodes.length === 0 && (
          <div className="graph-empty">
            <p>No graph data for this integration</p>
          </div>
        )}
        {!graphLoading && !graphError && nodes.length > 0 && (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            fitView
            attributionPosition="bottom-left"
          >
            <Background color="#E3F0FB" gap={16} />
            <Controls />
            <MiniMap nodeColor={() => '#0099FA'} maskColor="rgba(10, 42, 71, 0.1)" />
          </ReactFlow>
        )}
      </div>
    </div>
  );
};

export default FineTuningGraphPanel;
